import React, { useEffect, useState } from 'react';
import '../style/Groups.css';
import Avatar from './Avatar';
import { useSocket } from '../socket/SocketContext';
import { useLanguage } from '../i18n/LanguageContext';
import { useLayoutMode } from '../hooks/useLayoutMode';


const RESULT_KEY = {
  accepted: 'knock.resultAccepted',
  rejected: 'knock.resultRejected',
  expired: 'knock.resultExpired',
  cancelled: 'knock.resultCancelled'
};

function useCountdown(expiresAt) {
  const [secondsLeft, setSecondsLeft] = useState(0);
  useEffect(() => {
    if (!expiresAt) return undefined;
    const tick = () => setSecondsLeft(Math.max(0, Math.round((expiresAt - Date.now()) / 1000)));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);
  return secondsLeft;
}

// Handles both directions of the door-knocking flow, globally: the incoming
// prompt (accept/reject with a countdown) and the outgoing status (sent,
// waiting, cancel button) plus a one-shot toast once an outgoing knock
// resolves either way.
export default function KnockOverlay() {
  const { groupState, respondKnock, cancelKnock, pushToast, clearLastKnockResult } = useSocket();
  const { t } = useLanguage();
  const { isMobile } = useLayoutMode();
  const incoming = groupState.incomingKnocks[0];
  const outgoing = groupState.pendingOutgoingKnock;
  const incomingSecondsLeft = useCountdown(incoming && incoming.expiresAt);
  const outgoingSecondsLeft = useCountdown(outgoing && outgoing.expiresAt);

  useEffect(() => {
    if (!groupState.lastKnockResult) return;
    const key = RESULT_KEY[groupState.lastKnockResult.status];
    pushToast(key ? t(key) : t('knock.resultDefault'));
    clearLastKnockResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupState.lastKnockResult]);

  return (
    <>
      {incoming && (
        <div className="modal-container KnockModal">
          <div className="KnockModalContent">
            <Avatar imageUrl={incoming.from.imageUrl} alt={incoming.from.username} zoom
              style={{ width: 56, height: 56, borderRadius: 16, objectFit: 'cover' }} />
            <div>{t('knock.knockingOnDoor', { name: `${incoming.from.fullName || incoming.from.username}` })}</div>
            <div className="KnockCountdown">{t('knock.expiresIn', { seconds: incomingSecondsLeft })}</div>
            <div className="KnockModalActions">
              <button onClick={() => respondKnock(incoming.knockId, true)} className="SendButton primaryColor">{t('knock.letThemIn')}</button>
              <button onClick={() => respondKnock(incoming.knockId, false)} className="SendButton cancelColor">{t('knock.ignore')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Phones: a one-line chip above the nav/voice bar instead of the
          desktop's rail-sized card, which covered half the screen there. */}
      {!incoming && outgoing && isMobile && (
        <div className="KnockChip glass" role="status">
          <span className="KnockChipText">
            <b>{t('knock.knocking')}</b>
            <span className="descGray">{t('knock.waitingForAnswer', { seconds: outgoingSecondsLeft })}</span>
          </span>
          <button onClick={() => cancelKnock(outgoing.knockId)} className="SendButton secondaryColor KnockChipCancel">{t('knock.cancel')}</button>
        </div>
      )}

      {!incoming && outgoing && !isMobile && (
        <div className="VoicePanel glass">
          <div className="VoicePanelHeader"><span>{t('knock.knocking')}</span></div>
          <div className="KnockCountdown">{t('knock.waitingForAnswer', { seconds: outgoingSecondsLeft })}</div>
          <button onClick={() => cancelKnock(outgoing.knockId)} className="SendButton secondaryColor">{t('knock.cancel')}</button>
        </div>
      )}
    </>
  );
}
